<script setup lang="ts">
import CategoryBubble from '@/components/CategoryBubble.vue';
import { computed } from 'vue';
import { useAreas, useCategories } from '@ilteducation/content-ref-data';
import type { Area, Category } from '@ilteducation/content-ref-data-types';
import { Video } from '@ilteducation/videos';
import useGuiStore from '@/store/gui';
import { useGrades } from '@/composables/use-grades';
import { useLocale } from '@/plugins/translation';

const props = withDefaults(
  defineProps<{
    video: Video;
    subjectId: string;
    area?: Area;
    useThemeStyling?: boolean;
  }>(),
  {
    useThemeStyling: true,
  },
);

const { byId: getAreaById, isLoading: isLoadingAreas } = useAreas();
const { byId: getCategoryById, isLoading: isLoadingCategories } = useCategories();
const { gradeAbbreviation } = useGrades();
const { t } = useLocale();

const subject = computed(() => getCategoryById.value(props.subjectId));

const fallbackSubjectArea = computed(() =>
  (props.video?.orderedAreas || [])
    .flatMap((area) => area.id)
    .map(getAreaById.value)
    .find((a) => subject.value && props.subjectId === a?.categoryId),
);
const area = computed(() => props.area || fallbackSubjectArea.value);
const { getThemeName } = useGuiStore();

// Sorted by abbreviation so the badges and the spoken grade names come out in the same order.
const gradeCategories = computed(() =>
  props.video.categories
    .map((vc) => getCategoryById.value(vc))
    .filter((c): c is Category => !!c && c.kind === 'grade')
    .sort((a, b) => (gradeAbbreviation(a) > gradeAbbreviation(b) ? 1 : -1)),
);

const grades = computed(() => gradeCategories.value.map((c) => gradeAbbreviation(c)));

// The badges show short forms ("4-6", "Gy") that read poorly aloud, so they are hidden from assistive
// tech and the full grade names are put in the link's accessible name instead. The label has to keep
// starting with the title: it is the only text in the link left visible, and WCAG 2.5.3 requires the
// accessible name to contain the visible text.
const gradeNames = computed(() =>
  gradeCategories.value.map((c) => {
    const translationKey = `grades.${c.categoryCode}`;
    return t(translationKey) === translationKey ? c.category : t(translationKey);
  }),
);

const linkLabel = computed(() => [props.video.title, ...gradeNames.value].join(', '));

const areaNotFound = computed(() => {
  return !(isLoadingCategories.value || isLoadingAreas.value) && !area.value;
});
</script>

<template>
  <v-col v-if="!areaNotFound" cols="6" sm="4" md="3">
    <v-sheet
      v-if="props.video && subject && area"
      :theme="useThemeStyling ? getThemeName(subject.slug) : getThemeName('')"
      class="single-subject-video mb-2"
      :class="{ ['bg-transparent']: !useThemeStyling }"
    >
      <router-link
        :to="{
          name: 'showFilm',
          params: {
            subject: subject.slug,
            area: area?.slug,
            film: props.video.slug,
          },
        }"
        :aria-label="linkLabel"
      >
        <div class="image-subject-grade-wrapper pb-3">
          <v-img :src="props.video.thumbnail" alt="" :aspect-ratio="16 / 9">
            <template #placeholder>
              <!-- Decorative: the skeleton defaults to role="alert" + aria-label="Loading...", which both
                   spams a live region per card and counts as text inside the link. -->
              <v-skeleton-loader type="image" aria-hidden="true" />
            </template>
          </v-img>
          <v-img class="play-overlay" src="@/assets/images/icon-play-circle.svg" alt="" />
          <div class="subject-grade d-flex flex-column gr-lg-1">
            <CategoryBubble v-if="subject.categoryCode" :subject="subject" size="small" />
            <div v-for="(grade, index) in grades" :key="index" class="grade bg-secondary-container" aria-hidden="true">
              {{ grade }}
            </div>
          </div>
        </div>
        <div class="film-title text-h5" :class="useThemeStyling ? 'text-secondary underline' : 'text-secondary'">
          {{ props.video.title }}
        </div>
      </router-link>
    </v-sheet>
    <v-skeleton-loader
      style="aspect-ratio: 16 / 9; overflow: hidden; border-radius: 0"
      v-else
      type="image,text"
      color="transparent"
    ></v-skeleton-loader>
  </v-col>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';
@use 'sass:map';

.subject-grade {
  position: absolute;
  top: -8px;
  left: -10px;
  @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
    top: 10px;
  }

  .grade {
    display: inline-block;
    border-radius: 100%;
    font-size: 0.75rem;
    line-height: 1.25rem;
    height: 1.75rem;
    width: 1.75rem;
    padding: 0.25rem 0.1875rem;
    text-align: center;
    text-transform: capitalize;

    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
      font-weight: 500;
      line-height: 1.375rem;
      height: 2rem;
      width: 2rem;
      padding: 0.3125rem 0.1875rem;
    }
  }
}

.single-subject-video {
  a {
    text-decoration: none;
  }

  .image-subject-grade-wrapper {
    position: relative;

    img:first-of-type {
      border-radius: 4px;
      box-shadow: 0 4px 6px 0 rgba(18, 43, 64, 0.2);
    }

    .play-overlay {
      opacity: 0;
      position: absolute;
      width: 50px;
      height: 50px;
      margin: auto;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      filter: drop-shadow(1px 6px 12px rgba(18, 43, 64, 0.5));
      transition: opacity 0.3s ease-in-out;
    }

    &:hover {
      .play-overlay {
        opacity: 1;
      }
    }
  }
}

.film-title {
  font-size: 0.875rem !important;
  letter-spacing: 0.1px;
  @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
    font-size: 0.9375rem !important;
    letter-spacing: 0;
  }

  &.underline {
    text-decoration: none;
    border-bottom: 3px solid rgba(var(--v-theme-primary), 0.1);
    display: inline;
    line-height: 1.6;
    transition: all 0.1s ease;
  }
}
</style>
